import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import {
  ThreeDSegmentation,
  ThreeDSegmentationPage,
  useGetAnnotationSetOwnersFor3DQuery,
  useGetAbbottStudiesFor3DQuery,
  useLazyGetDicomsFor3DQuery,
  useLazyGetFramesFor3DQuery,
  useLazyGetSegmentationLabelsFor3DQuery,
  useLazyGetSegmentationPredictionsFor3DQuery,
  useLazyGetSegmentationReferencesFor3DQuery,
} from '../../redux/slices/threeDStudy';
import { config } from '../../services/config';
import {
  useGetDismissedThreeDReviewFramesQuery,
  useGetThreeDReviewFramesQuery,
  useUpdateReviewFrameMutation,
} from '../../redux/slices/reviews';
import {
  AnatomyBar,
  AnnotationOwnerChoice,
  AnnotationOwnerChoices,
  AnnotationSetOwner,
  ButtonGroup,
  Fallback,
  FramePreviewCloseButton,
  FramePreviewHeader,
  FramePreviewImage,
  FramePreviewLink,
  FramePreviewMedia,
  FramePreviewMessage,
  FramePreviewMetadata,
  FramePreviewOverlay,
  FramePreviewPanel,
  FrameReviewActions,
  FrameReviewButton,
  FrameReviewForm,
  Hint,
  ReviewButton,
  ReviewCardMessage,
  ReviewList,
  ReviewListGroup,
  ReviewListMessage,
  ReviewListSection,
  ReviewListTitle,
  ReviewType,
  SegmentationLoadGroup,
  SegmentationPageCount,
  StudyLabel,
  StudyPanel,
  StudySelector,
  StudyWarning,
  ToggleButton,
  TopBar,
  ViewerPage,
  ViewerShell,
  ViewerWorkspace,
} from './styled';

const PAGE_SIZE = 100;
const SEGMENTATION_BATCH_SIZE = 1000;
const IMAGE_WIDTH = 630;
const SCALE = 0.01;
// Modal spacing for ABBOTT 630x405 DICOMs. Some older studies omit this metadata.
const DEFAULT_ABBOTT_PHYSICAL_DELTA_X = 0.03059332669;
const SURFACE_POINTS_PER_CONTOUR = 12;
const MAX_SURFACE_POINTS = 600;
const IMAGE_HEIGHT = 405;
const LABELER_URL = (config.LABELER_URL || 'https://labeler.icardio.ai').replace(/\/$/, '');

type Source = 'prediction' | 'technician' | 'reference';
type SourceStatus = 'loading' | 'complete' | 'error';
type DicomLoadStatus = 'idle' | 'loading' | 'complete' | 'error';
type FramePreviewStatus = 'loading' | 'complete' | 'error';

interface FramePreviewState {
  color: string;
  dicomIcid: string;
  file: string | null;
  frameIndex: number;
  paths: string[];
  segmentationClass: string;
  status: FramePreviewStatus;
  studyIcid: string;
  userEmail: string | null;
}

interface SegmentationLoaderState {
  buffers: Record<Source, ThreeDSegmentation[]>;
  errors: Record<Source, boolean>;
  hasNext: Record<Source, boolean>;
  key: string;
  loadedCount: number;
  pages: Record<Source, number>;
}

const sources: Array<{ id: Source; label: string; color: string }> = [
  { id: 'technician', label: 'TECHNICIAN', color: '#ffc857' },
  { id: 'reference', label: 'REFERENCE', color: '#eaa4ff' },
  { id: 'prediction', label: 'PREDICTION', color: '#76d7ff' },
];

const initialSourceVisibility = (): Record<Source, boolean> => ({
  technician: true,
  reference: true,
  prediction: false,
});

const emptySegmentations = (): Record<Source, ThreeDSegmentation[]> => ({
  technician: [],
  reference: [],
  prediction: [],
});

const initialSourceStatuses = (): Record<Source, SourceStatus> => ({
  technician: 'loading',
  reference: 'loading',
  prediction: 'loading',
});

const initialSourceTotals = (): Record<Source, number | undefined> => ({
  technician: undefined,
  reference: undefined,
  prediction: undefined,
});

const createSegmentationLoaderState = (
  key = '',
  predictionsEnabled = false
): SegmentationLoaderState => ({
  buffers: emptySegmentations(),
  errors: { technician: false, reference: false, prediction: false },
  hasNext: {
    technician: Boolean(key),
    reference: Boolean(key),
    prediction: Boolean(key) && predictionsEnabled,
  },
  key,
  loadedCount: 0,
  pages: { technician: 1, reference: 1, prediction: 1 },
});

const disposeGroup = (group: THREE.Group) => {
  group.children.forEach(child => {
    const line = child as THREE.Line;
    line.geometry?.dispose();
    if (line.material) {
      (Array.isArray(line.material) ? line.material : [line.material]).forEach(material =>
        material.dispose()
      );
    }
  });
  group.clear();
};

// Abbott PNO matrices are column-major. These corrections match the Labeler 3D viewer.
const buildPnoMatrixSegmentation = (elements: number[], scaleFactor: number) => {
  const translationScale = (SCALE * scaleFactor) / 10;
  const pnoMatrix = new THREE.Matrix4();
  pnoMatrix.set(
    elements[0],
    elements[4],
    elements[8],
    elements[12] * translationScale,
    elements[1],
    elements[5],
    elements[9],
    elements[13] * translationScale,
    elements[2],
    elements[6],
    elements[10],
    elements[14] * translationScale,
    elements[3],
    elements[7],
    elements[11],
    elements[15]
  );

  let matrix = new THREE.Matrix4().makeTranslation(-(IMAGE_WIDTH * SCALE) / 2, 0, 0);
  matrix = new THREE.Matrix4().multiplyMatrices(
    new THREE.Matrix4().makeTranslation(0, 22 * SCALE, 0),
    matrix
  );
  matrix = new THREE.Matrix4().multiplyMatrices(
    new THREE.Matrix4().makeRotationZ(Math.PI / 2),
    matrix
  );
  matrix = new THREE.Matrix4().multiplyMatrices(
    new THREE.Matrix4().makeRotationY(Math.PI / 2),
    matrix
  );
  return new THREE.Matrix4().multiplyMatrices(pnoMatrix, matrix);
};

const getSegmentationPaths = (segmentation: ThreeDSegmentation) =>
  Array.isArray(segmentation.paths) ? segmentation.paths : [];

const getFrameKey = (dicomIcid: string, frameIndex: number) => `${dicomIcid}:${frameIndex}`;

const ThreeDStudyViewer = () => {
  const navigate = useNavigate();
  const { studyIcid: routeStudyIcid } = useParams<{ studyIcid?: string }>();
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const sourceGroupsRef = useRef<Record<Source, THREE.Group> | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const lastFittedStudyIcidRef = useRef<string | null>(null);
  const [sourceVisibility, setSourceVisibility] = useState(initialSourceVisibility);
  const predictionsEnabled = sourceVisibility.prediction;
  const [anatomyVisibility, setAnatomyVisibility] = useState<Record<string, boolean>>({});
  const [allAnatomyHidden, setAllAnatomyHidden] = useState(false);
  const sourceVisibilityRef = useRef(sourceVisibility);
  const anatomyVisibilityRef = useRef(anatomyVisibility);
  const surfaceVisibilityRef = useRef(true);
  const segmentationLoaderRef = useRef<SegmentationLoaderState>(createSegmentationLoaderState());
  const [isWebGlAvailable, setIsWebGlAvailable] = useState(true);
  const [surfacesVisible, setSurfacesVisible] = useState(true);
  const [inReviewFramesVisible, setInReviewFramesVisible] = useState(true);
  const [studyIcid, setStudyIcid] = useState(routeStudyIcid ?? '');
  const [segmentationLimit, setSegmentationLimit] = useState(SEGMENTATION_BATCH_SIZE);
  const [framePreview, setFramePreview] = useState<FramePreviewState | null>(null);
  const [isReviewFormOpen, setIsReviewFormOpen] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [selectedAnnotationSetUuid, setSelectedAnnotationSetUuid] = useState('');
  const [previewImageDimensions, setPreviewImageDimensions] = useState({
    width: IMAGE_WIDTH,
    height: IMAGE_HEIGHT,
  });
  const [dicomLoadStatus, setDicomLoadStatus] = useState<DicomLoadStatus>('idle');
  const [segmentationsBySource, setSegmentationsBySource] = useState(emptySegmentations);
  const [sourceStatuses, setSourceStatuses] = useState(initialSourceStatuses);
  const [sourceTotals, setSourceTotals] = useState(initialSourceTotals);
  const [physicalDeltaXByDicomIcid, setPhysicalDeltaXByDicomIcid] = useState<
    Record<string, number | null>
  >({});
  const [loadLabels] = useLazyGetSegmentationLabelsFor3DQuery();
  const [loadPredictions] = useLazyGetSegmentationPredictionsFor3DQuery();
  const [loadReferences] = useLazyGetSegmentationReferencesFor3DQuery();
  const [loadDicoms] = useLazyGetDicomsFor3DQuery();
  const [loadFramesFor3D] = useLazyGetFramesFor3DQuery();
  const [updateReviewFrame, { isLoading: isSavingReview }] = useUpdateReviewFrameMutation();
  const {
    data: abbottStudies = [],
    isLoading: areStudiesLoading,
    isError: studiesHaveError,
  } = useGetAbbottStudiesFor3DQuery();
  const {
    data: annotationSetOwners = [],
    isLoading: areAnnotationSetOwnersLoading,
    isError: annotationSetOwnersHaveError,
  } = useGetAnnotationSetOwnersFor3DQuery(studyIcid, { skip: !studyIcid });
  const {
    data: threeDReviews = [],
    isLoading: areReviewsLoading,
    isError: reviewsHaveError,
  } = useGetThreeDReviewFramesQuery(studyIcid, { skip: !studyIcid });
  const {
    data: dismissedThreeDReviews = [],
    isLoading: areDismissedReviewsLoading,
    isError: dismissedReviewsHaveError,
  } = useGetDismissedThreeDReviewFramesQuery(studyIcid, { skip: !studyIcid });
  const isLoading = Object.values(sourceStatuses).some(status => status === 'loading');
  const hasLoadError = Object.values(sourceStatuses).some(status => status === 'error');
  const inReviewFrameKeys = useMemo(
    () => new Set(threeDReviews.map(review => getFrameKey(review.dicomIcid, review.frameIndex))),
    [threeDReviews]
  );

  useEffect(() => {
    setSelectedAnnotationSetUuid(current =>
      annotationSetOwners.some(owner => owner.uuid === current)
        ? current
        : annotationSetOwners[0]?.uuid || ''
    );
  }, [annotationSetOwners]);

  const openFramePreview = useCallback(
    ({
      color,
      dicomIcid,
      frameIndex,
      paths,
      segmentationClass,
      selectedStudyIcid,
      userEmail,
    }: {
      color: string;
      dicomIcid: string;
      frameIndex: number;
      paths: string[];
      segmentationClass: string;
      selectedStudyIcid: string;
      userEmail: string | null;
    }) => {
      setPreviewImageDimensions({ width: IMAGE_WIDTH, height: IMAGE_HEIGHT });
      setIsReviewFormOpen(false);
      setReviewMessage('');
      setReviewError(null);
      setFramePreview({
        color,
        dicomIcid,
        file: null,
        frameIndex,
        paths,
        segmentationClass,
        status: 'loading',
        studyIcid: selectedStudyIcid,
        userEmail,
      });
      void loadFramesFor3D(dicomIcid, true)
        .unwrap()
        .then(frames => {
          const file = frames.find(frame => frame.index === frameIndex)?.file ?? null;
          setFramePreview(current =>
            current?.dicomIcid === dicomIcid && current.frameIndex === frameIndex
              ? { ...current, file, status: file ? 'complete' : 'error' }
              : current
          );
        })
        .catch(() => {
          setFramePreview(current =>
            current?.dicomIcid === dicomIcid && current.frameIndex === frameIndex
              ? { ...current, status: 'error' }
              : current
          );
        });
    },
    [loadFramesFor3D]
  );

  useEffect(() => {
    if (!abbottStudies.length) return;
    const requestedStudyIcid = routeStudyIcid || studyIcid;
    const nextStudyIcid = abbottStudies.some(study => study.icid === requestedStudyIcid)
      ? requestedStudyIcid
      : abbottStudies[0].icid;

    if (studyIcid !== nextStudyIcid) {
      setSegmentationLimit(SEGMENTATION_BATCH_SIZE);
      setStudyIcid(nextStudyIcid);
    }
    if (routeStudyIcid !== nextStudyIcid) {
      navigate(`/3d-study-viewer/${encodeURIComponent(nextStudyIcid)}`, { replace: true });
    }
  }, [abbottStudies, navigate, routeStudyIcid, studyIcid]);

  useEffect(() => {
    if (!studyIcid) {
      segmentationLoaderRef.current = createSegmentationLoaderState();
      setSegmentationsBySource(emptySegmentations());
      setSourceStatuses({ technician: 'complete', reference: 'complete', prediction: 'complete' });
      setSourceTotals(initialSourceTotals());
      return;
    }

    let cancelled = false;
    const loaderKey = `${studyIcid}:${predictionsEnabled ? 'predictions' : 'no-predictions'}`;
    if (segmentationLoaderRef.current.key !== loaderKey) {
      segmentationLoaderRef.current = createSegmentationLoaderState(loaderKey, predictionsEnabled);
      setSegmentationsBySource(emptySegmentations());
      setSourceTotals(initialSourceTotals());
    }
    const loader = segmentationLoaderRef.current;
    const enabledSources = sources
      .map(({ id }) => id)
      .filter(source => source !== 'prediction' || predictionsEnabled);

    setSourceStatuses(
      Object.fromEntries(
        sources.map(({ id }) => [
          id,
          !enabledSources.includes(id) ? 'complete' : loader.errors[id] ? 'error' : 'loading',
        ])
      ) as Record<Source, SourceStatus>
    );

    const loadPage = (source: Source): Promise<ThreeDSegmentationPage> => {
      const params = { studyIcid, page: loader.pages[source], size: PAGE_SIZE };
      if (source === 'technician') return loadLabels(params, true).unwrap();
      if (source === 'reference') return loadReferences(params, true).unwrap();
      return loadPredictions(params, true).unwrap();
    };

    const loadPages = async () => {
      while (loader.loadedCount < segmentationLimit && !cancelled) {
        const additions = emptySegmentations();
        enabledSources.forEach(source => {
          const remaining = segmentationLimit - loader.loadedCount;
          if (remaining <= 0) return;
          const records = loader.buffers[source].slice(0, remaining);
          additions[source] = records;
          loader.buffers[source] = loader.buffers[source].slice(records.length);
          loader.loadedCount += records.length;
        });

        if (Object.values(additions).some(records => records.length > 0)) {
          setSegmentationsBySource(current => ({
            technician: [...current.technician, ...additions.technician],
            reference: [...current.reference, ...additions.reference],
            prediction: [...current.prediction, ...additions.prediction],
          }));
        }
        if (loader.loadedCount >= segmentationLimit) break;

        const sourcesToFetch = enabledSources.filter(
          source => loader.buffers[source].length === 0 && loader.hasNext[source]
        );
        if (sourcesToFetch.length === 0) break;

        const results = await Promise.all(
          sourcesToFetch.map(async source => {
            try {
              return { source, page: await loadPage(source) };
            } catch {
              return { source, page: undefined };
            }
          })
        );
        if (cancelled) return;

        results.forEach(({ source, page }) => {
          if (!page) {
            loader.hasNext[source] = false;
            loader.errors[source] = true;
            return;
          }
          loader.buffers[source] = page.results;
          loader.hasNext[source] = Boolean(page.next);
          loader.pages[source] += 1;
          setSourceTotals(current => ({ ...current, [source]: page.count }));
        });
      }
      if (!cancelled) {
        setSourceStatuses(
          Object.fromEntries(
            sources.map(({ id }) => [id, loader.errors[id] ? 'error' : 'complete'])
          ) as Record<Source, SourceStatus>
        );
      }
    };

    loadPages();
    return () => {
      cancelled = true;
    };
  }, [
    loadLabels,
    loadPredictions,
    loadReferences,
    predictionsEnabled,
    segmentationLimit,
    studyIcid,
  ]);

  useEffect(() => {
    if (!studyIcid) {
      setPhysicalDeltaXByDicomIcid({});
      setFramePreview(null);
      setDicomLoadStatus('idle');
      return;
    }

    let cancelled = false;
    let page = 1;
    let hasNext = true;
    let loadFailed = false;
    setPhysicalDeltaXByDicomIcid({});
    setFramePreview(null);
    setDicomLoadStatus('loading');

    const loadDicomPages = async () => {
      while (!cancelled && hasNext) {
        try {
          const response = await loadDicoms({
            studyIcid,
            page,
            size: PAGE_SIZE,
          }).unwrap();
          if (cancelled) return;
          setPhysicalDeltaXByDicomIcid(current => ({
            ...current,
            ...Object.fromEntries(
              response.results.map(dicom => [dicom.icid, dicom.physicalDeltaX] as const)
            ),
          }));
          hasNext = Boolean(response.next);
          page += 1;
        } catch {
          hasNext = false;
          loadFailed = true;
        }
      }
      if (!cancelled) setDicomLoadStatus(loadFailed ? 'error' : 'complete');
    };

    loadDicomPages();
    return () => {
      cancelled = true;
    };
  }, [loadDicoms, studyIcid]);

  const classColors = useMemo(() => {
    const names = Array.from(
      new Set(
        Object.values(segmentationsBySource)
          .flat()
          .map(segmentation => segmentation.segmentationClassValue || 'Unknown')
      )
    ).sort();
    return Object.fromEntries(
      names.map((name, index) => [
        name,
        `hsl(${(index * 360 + 255) / Math.max(names.length, 1)}, 80%, 70%)`,
      ])
    );
  }, [segmentationsBySource]);

  useEffect(() => {
    sourceVisibilityRef.current = sourceVisibility;
  }, [sourceVisibility]);

  useEffect(() => {
    anatomyVisibilityRef.current = anatomyVisibility;
  }, [anatomyVisibility]);

  useEffect(() => {
    surfaceVisibilityRef.current = surfacesVisible;
  }, [surfacesVisible]);

  useEffect(() => {
    setAnatomyVisibility(current => {
      const next = { ...current };
      Object.keys(classColors).forEach(className => {
        if (!(className in next)) next[className] = !allAnatomyHidden;
      });
      return next;
    });
  }, [allAnatomyHidden, classColors]);

  useEffect(() => {
    const host = canvasHostRef.current;
    if (!host) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      setIsWebGlAvailable(false);
      return;
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#0c1724');
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(4.5, 3.5, 6.5);
    cameraRef.current = camera;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    host.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 2;
    controls.maxDistance = 22;
    controlsRef.current = controls;
    scene.add(new THREE.HemisphereLight('#c9e8ff', '#142131', 2.2));

    const groups = {} as Record<Source, THREE.Group>;
    sources.forEach(({ id }) => {
      const group = new THREE.Group();
      groups[id] = group;
      scene.add(group);
    });
    sourceGroupsRef.current = groups;

    const raycaster = new THREE.Raycaster();
    raycaster.params.Line.threshold = 0.08;
    const pointer = new THREE.Vector2();
    let pointerDown: { x: number; y: number } | null = null;
    let hoveredContour: THREE.Object3D | null = null;
    let hoverFrame = 0;
    let pendingPointer: { x: number; y: number } | null = null;

    const getVisibleContours = () =>
      Object.values(groups).flatMap(group =>
        group.visible
          ? group.children.filter(child => child.visible && child.userData.kind === 'contour')
          : []
      );
    const getContourAt = (clientX: number, clientY: number) => {
      const bounds = renderer.domElement.getBoundingClientRect();
      pointer.set(
        ((clientX - bounds.left) / bounds.width) * 2 - 1,
        -((clientY - bounds.top) / bounds.height) * 2 + 1
      );
      raycaster.setFromCamera(pointer, camera);
      return raycaster.intersectObjects(getVisibleContours(), false)[0]?.object ?? null;
    };
    const setHoveredContour = (nextContour: THREE.Object3D | null) => {
      if (hoveredContour === nextContour) return;
      hoveredContour = nextContour;
      Object.values(groups).forEach(group => {
        group.children.forEach(child => {
          const renderable = child as THREE.Line | THREE.Mesh;
          const materials = Array.isArray(renderable.material)
            ? renderable.material
            : [renderable.material];
          materials.forEach(material => {
            if (!material) return;
            const displayMaterial = material as THREE.Material & {
              color?: THREE.Color;
              opacity?: number;
            };
            if (typeof displayMaterial.opacity === 'number') {
              displayMaterial.opacity = nextContour
                ? child === nextContour
                  ? 1
                  : child.userData.kind === 'surface'
                    ? 0.08
                    : 0.12
                : child.userData.baseOpacity;
            }
            if (
              displayMaterial.color instanceof THREE.Color &&
              typeof child.userData.baseColor === 'number'
            ) {
              displayMaterial.color.set(
                child === nextContour ? '#ffffff' : child.userData.baseColor
              );
            }
          });
        });
      });
      renderer.domElement.style.cursor = nextContour ? 'pointer' : 'grab';
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.button === 0) {
        pointerDown = { x: event.clientX, y: event.clientY };
        setHoveredContour(null);
        renderer.domElement.style.cursor = 'grabbing';
      }
    };
    const handlePointerUp = (event: PointerEvent) => {
      if (!pointerDown || event.button !== 0) return;
      const movement = Math.hypot(event.clientX - pointerDown.x, event.clientY - pointerDown.y);
      pointerDown = null;
      if (movement > 5) {
        renderer.domElement.style.cursor = 'grab';
        return;
      }

      const hit = getContourAt(event.clientX, event.clientY);
      setHoveredContour(hit);
      if (
        typeof hit?.userData.dicomIcid === 'string' &&
        typeof hit.userData.studyIcid === 'string' &&
        Number.isInteger(hit.userData.frameIndex)
      ) {
        const dicomIcid = hit.userData.dicomIcid as string;
        const frameIndex = hit.userData.frameIndex as number;
        const selectedStudyIcid = hit.userData.studyIcid as string;
        const paths = Array.isArray(hit.userData.paths) ? hit.userData.paths : [];
        const color =
          typeof hit.userData.overlayColor === 'string' ? hit.userData.overlayColor : '#ffffff';
        const segmentationClass =
          typeof hit.userData.anatomy === 'string' ? hit.userData.anatomy : 'Unknown';
        const userEmail =
          typeof hit.userData.userEmail === 'string' ? hit.userData.userEmail : null;
        openFramePreview({
          color,
          dicomIcid,
          frameIndex,
          paths,
          segmentationClass,
          selectedStudyIcid,
          userEmail,
        });
      }
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (pointerDown) return;
      pendingPointer = { x: event.clientX, y: event.clientY };
      if (hoverFrame) return;
      hoverFrame = requestAnimationFrame(() => {
        hoverFrame = 0;
        if (!pendingPointer) return;
        setHoveredContour(getContourAt(pendingPointer.x, pendingPointer.y));
      });
    };
    const handlePointerCancel = () => {
      pointerDown = null;
      setHoveredContour(null);
    };
    const handlePointerLeave = () => {
      pendingPointer = null;
      setHoveredContour(null);
    };
    renderer.domElement.addEventListener('pointerdown', handlePointerDown);
    renderer.domElement.addEventListener('pointerup', handlePointerUp);
    renderer.domElement.addEventListener('pointermove', handlePointerMove);
    renderer.domElement.addEventListener('pointercancel', handlePointerCancel);
    renderer.domElement.addEventListener('pointerleave', handlePointerLeave);

    const resize = () => {
      const { clientWidth: width, clientHeight: height } = host;
      if (!width || !height) return;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(host);
    resize();

    let animationFrame = 0;
    const render = () => {
      animationFrame = requestAnimationFrame(render);
      controls.update();
      renderer.render(scene, camera);
    };
    render();

    return () => {
      cancelAnimationFrame(animationFrame);
      cancelAnimationFrame(hoverFrame);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
      renderer.domElement.removeEventListener('pointerup', handlePointerUp);
      renderer.domElement.removeEventListener('pointermove', handlePointerMove);
      renderer.domElement.removeEventListener('pointercancel', handlePointerCancel);
      renderer.domElement.removeEventListener('pointerleave', handlePointerLeave);
      controls.dispose();
      Object.values(groups).forEach(disposeGroup);
      renderer.dispose();
      renderer.domElement.remove();
      sourceGroupsRef.current = null;
      controlsRef.current = null;
      cameraRef.current = null;
    };
  }, [openFramePreview]);

  useEffect(() => {
    const sourceGroups = sourceGroupsRef.current;
    if (!sourceGroups) return;
    const segmentationCount = Object.values(segmentationsBySource).reduce(
      (total, segmentations) => total + segmentations.length,
      0
    );
    const expectedLoaderKey = `${studyIcid}:${predictionsEnabled ? 'predictions' : 'no-predictions'}`;
    if (
      segmentationLoaderRef.current.key !== expectedLoaderKey ||
      segmentationLoaderRef.current.loadedCount !== segmentationCount
    ) {
      return;
    }
    const svgLoader = new SVGLoader();

    sources.forEach(({ id }) => {
      const group = sourceGroups[id];
      const surfacePointsByAnatomy = new Map<string, THREE.Vector3[]>();
      disposeGroup(group);
      group.visible = sourceVisibilityRef.current[id];
      segmentationsBySource[id].forEach(segmentation => {
        if (
          !inReviewFramesVisible &&
          segmentation.dicomIcid &&
          inReviewFrameKeys.has(getFrameKey(segmentation.dicomIcid, segmentation.frameIndex))
        ) {
          return;
        }
        if (!segmentation.pno || segmentation.pno.length !== 16) return;
        if (
          !segmentation.dicomIcid ||
          !Object.prototype.hasOwnProperty.call(physicalDeltaXByDicomIcid, segmentation.dicomIcid)
        ) {
          return;
        }
        const storedPhysicalDeltaX = physicalDeltaXByDicomIcid[segmentation.dicomIcid];
        const physicalDeltaX =
          typeof storedPhysicalDeltaX === 'number' && storedPhysicalDeltaX > 0
            ? storedPhysicalDeltaX
            : DEFAULT_ABBOTT_PHYSICAL_DELTA_X;
        const matrix = buildPnoMatrixSegmentation(segmentation.pno, 1 / physicalDeltaX);
        const className = segmentation.segmentationClassValue || 'Unknown';
        const color = new THREE.Color(classColors[className]);
        getSegmentationPaths(segmentation).forEach(pathString => {
          try {
            const svg = svgLoader.parse(
              `<svg xmlns="http://www.w3.org/2000/svg"><path d="${pathString.replace(/,/g, ' ')}" /></svg>`
            );
            svg.paths.forEach(path => {
              path.toShapes(true).forEach(shape => {
                const points = shape
                  .getPoints(100)
                  .map(point => new THREE.Vector3(point.x * SCALE, -point.y * SCALE, 0))
                  .map(point => point.applyMatrix4(matrix));
                const geometry = new THREE.BufferGeometry().setFromPoints(points);
                const surfacePoints = surfacePointsByAnatomy.get(className) ?? [];
                const sampleStep = Math.max(
                  1,
                  Math.floor(points.length / SURFACE_POINTS_PER_CONTOUR)
                );
                for (let index = 0; index < points.length; index += sampleStep) {
                  surfacePoints.push(points[index]);
                }
                surfacePointsByAnatomy.set(className, surfacePoints);
                const line = new THREE.LineLoop(
                  geometry,
                  new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.9 })
                );
                line.userData.anatomy = className;
                line.userData.baseColor = color.getHex();
                line.userData.baseOpacity = 0.9;
                line.userData.dicomIcid = segmentation.dicomIcid;
                line.userData.frameIndex = segmentation.frameIndex;
                line.userData.kind = 'contour';
                line.userData.overlayColor = `#${color.getHexString()}`;
                line.userData.paths = getSegmentationPaths(segmentation);
                line.userData.studyIcid = studyIcid;
                line.userData.userEmail = segmentation.userEmail ?? null;
                line.visible =
                  !surfaceVisibilityRef.current &&
                  (anatomyVisibilityRef.current[className] ?? true);
                line.renderOrder = 1;
                group.add(line);
              });
            });
          } catch {
            // A malformed SVG path should not prevent the remainder of the study from rendering.
          }
        });
      });

      surfacePointsByAnatomy.forEach((points, className) => {
        if (points.length < 4) return;
        const sampleStep = Math.max(1, Math.ceil(points.length / MAX_SURFACE_POINTS));
        const sampledPoints = points.filter((_, index) => index % sampleStep === 0);
        try {
          const geometry = new ConvexGeometry(sampledPoints);
          if (geometry.getAttribute('position').count === 0) {
            geometry.dispose();
            return;
          }
          const surface = new THREE.Mesh(
            geometry,
            new THREE.MeshStandardMaterial({
              color: new THREE.Color(classColors[className]),
              depthWrite: false,
              opacity: 0.92,
              roughness: 0.7,
              side: THREE.DoubleSide,
              transparent: true,
            })
          );
          surface.userData.anatomy = className;
          surface.userData.baseColor = new THREE.Color(classColors[className]).getHex();
          surface.userData.baseOpacity = 0.92;
          surface.userData.kind = 'surface';
          surface.visible =
            surfaceVisibilityRef.current && (anatomyVisibilityRef.current[className] ?? true);
          group.add(surface);
        } catch {
          // Coplanar or otherwise degenerate contours cannot form a closed 3D surface.
        }
      });
    });

    const box = new THREE.Box3();
    box.makeEmpty();
    Object.values(sourceGroups).forEach(group => box.union(new THREE.Box3().setFromObject(group)));
    if (
      !box.isEmpty() &&
      controlsRef.current &&
      cameraRef.current &&
      dicomLoadStatus === 'complete' &&
      !isLoading &&
      lastFittedStudyIcidRef.current !== studyIcid
    ) {
      const center = box.getCenter(new THREE.Vector3());
      const radius = Math.max(box.getSize(new THREE.Vector3()).length() / 2, 1);
      controlsRef.current.target.copy(center);
      cameraRef.current.position
        .copy(center)
        .add(new THREE.Vector3(radius * 1.8, radius * 1.25, radius * 2));
      controlsRef.current.update();
      lastFittedStudyIcidRef.current = studyIcid;
    }
  }, [
    classColors,
    dicomLoadStatus,
    inReviewFrameKeys,
    inReviewFramesVisible,
    isLoading,
    physicalDeltaXByDicomIcid,
    predictionsEnabled,
    segmentationsBySource,
    studyIcid,
  ]);

  useEffect(() => {
    const sourceGroups = sourceGroupsRef.current;
    if (!sourceGroups) return;
    sources.forEach(({ id }) => {
      sourceGroups[id].visible = sourceVisibility[id];
    });
    Object.values(sourceGroups).forEach(group => {
      group.children.forEach(child => {
        const anatomyIsVisible = anatomyVisibility[child.userData.anatomy] ?? true;
        child.visible =
          anatomyIsVisible &&
          (child.userData.kind === 'surface' ? surfacesVisible : !surfacesVisible);
      });
    });
  }, [sourceVisibility, anatomyVisibility, surfacesVisible]);

  const anatomyNames = Object.keys(classColors);
  const dicomIcids = Object.keys(physicalDeltaXByDicomIcid);
  const missingPhysicalDeltaCount = dicomIcids.filter(dicomIcid => {
    const physicalDeltaX = physicalDeltaXByDicomIcid[dicomIcid];
    return typeof physicalDeltaX !== 'number' || physicalDeltaX <= 0;
  }).length;
  const displayedSegmentationCount = Object.values(segmentationsBySource).flat().length;
  const annotationSetOwnerNames = Array.from(
    new Set(annotationSetOwners.map(owner => owner.userName || owner.userEmail))
  );
  const hasMoreSegmentations = sources.some(({ id }) => {
    const total = sourceTotals[id];
    return (
      (id !== 'prediction' || predictionsEnabled) &&
      typeof total === 'number' &&
      total > segmentationsBySource[id].length
    );
  });
  const selectedFrameReview = framePreview
    ? [...threeDReviews, ...dismissedThreeDReviews].find(
        review =>
          review.dicomIcid === framePreview.dicomIcid &&
          review.frameIndex === framePreview.frameIndex
      )
    : undefined;

  const reviewSections = [
    {
      ariaLabel: '3D frame reviews',
      emptyMessage: 'No 3D frame reviews yet.',
      error: reviewsHaveError,
      isLoading: areReviewsLoading,
      reviews: threeDReviews,
      title: 'REVIEWS',
    },
    {
      ariaLabel: 'Dismissed 3D frame reviews',
      emptyMessage: 'No dismissed reviews.',
      error: dismissedReviewsHaveError,
      isLoading: areDismissedReviewsLoading,
      reviews: dismissedThreeDReviews,
      title: 'DISMISSED REVIEWS',
    },
  ];

  const openReviewPreview = (review: (typeof threeDReviews)[number]) => {
    const segmentation = Object.values(segmentationsBySource)
      .flat()
      .find(item => item.dicomIcid === review.dicomIcid && item.frameIndex === review.frameIndex);
    const segmentationClass = segmentation?.segmentationClassValue || 'Frame review';
    openFramePreview({
      color: classColors[segmentationClass] || '#9ee6b8',
      dicomIcid: review.dicomIcid,
      frameIndex: review.frameIndex,
      paths: segmentation ? getSegmentationPaths(segmentation) : [],
      segmentationClass,
      selectedStudyIcid: review.studyIcid,
      userEmail: segmentation?.userEmail ?? null,
    });
  };

  const handleReviewFrame = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!framePreview) return;
    if (!selectedAnnotationSetUuid) {
      setReviewError('Select an annotation set owner before saving.');
      return;
    }
    // Saving invalidates the active-review query and rebuilds the scene geometry.
    // Preserve the user's current camera instead of allowing that refresh to auto-fit it.
    lastFittedStudyIcidRef.current = studyIcid;
    setReviewError(null);
    try {
      await updateReviewFrame({
        annotationSet: selectedAnnotationSetUuid,
        comment: reviewMessage.trim(),
        dicomIcid: framePreview.dicomIcid,
        decision: '3D',
        frameIndex: framePreview.frameIndex,
        status: 'IN_REVIEW',
      }).unwrap();
      setIsReviewFormOpen(false);
    } catch {
      setReviewError('Could not save this frame review. Please try again.');
    }
  };

  return (
    <ViewerPage>
      <h1>3D Study Viewer</h1>
      <p>
        Static PNO-aligned segmentation contours{studyIcid ? ` for ${studyIcid}` : ''}. Drag to
        rotate, scroll to zoom, and right-drag to pan.
      </p>
      <ViewerWorkspace>
        <StudyPanel>
          <StudySelector>
            ABBOTT study
            <select
              aria-label="ABBOTT study"
              value={studyIcid}
              disabled={areStudiesLoading || abbottStudies.length === 0}
              onChange={event => {
                setSegmentationLimit(SEGMENTATION_BATCH_SIZE);
                navigate(`/3d-study-viewer/${encodeURIComponent(event.target.value)}`);
              }}
            >
              {areStudiesLoading && <option>Loading ABBOTT studies…</option>}
              {!areStudiesLoading && studiesHaveError && (
                <option>Could not load ABBOTT studies</option>
              )}
              {!areStudiesLoading && !studiesHaveError && abbottStudies.length === 0 && (
                <option>No ABBOTT studies available</option>
              )}
              {abbottStudies.map(study => (
                <option key={study.icid} value={study.icid}>
                  {study.foreignId || study.icid}
                </option>
              ))}
            </select>
          </StudySelector>
          <AnnotationSetOwner aria-live="polite">
            <span>Annotation set owner</span>
            <strong>
              {areAnnotationSetOwnersLoading
                ? 'Loading…'
                : annotationSetOwnersHaveError
                  ? 'Could not load owner'
                  : annotationSetOwnerNames.length > 0
                    ? annotationSetOwnerNames.join(', ')
                    : 'No owner found'}
            </strong>
          </AnnotationSetOwner>
          {dicomLoadStatus === 'complete' && missingPhysicalDeltaCount > 0 && (
            <StudyWarning role="status">
              Estimated spacing is in use: {missingPhysicalDeltaCount}{' '}
              {missingPhysicalDeltaCount === 1 ? 'DICOM is' : 'DICOMs are'} missing physical delta.
              3D positioning may be approximate.
            </StudyWarning>
          )}
          <ReviewListSection aria-label="3D frame reviews">
            <ReviewList>
              {reviewSections.map(section => (
                <ReviewListGroup key={section.title} aria-label={section.ariaLabel}>
                  <ReviewListTitle>
                    {section.title} · {section.reviews.length}
                  </ReviewListTitle>
                  {section.reviews.map(review => {
                    const isSelected =
                      framePreview?.dicomIcid === review.dicomIcid &&
                      framePreview.frameIndex === review.frameIndex;
                    const reviewCardMessage = review.comment || review.reviewerComment;
                    return (
                      <ReviewButton
                        key={review.uuid || `${review.dicomIcid}-${review.frameIndex}`}
                        type="button"
                        $active={isSelected}
                        aria-pressed={isSelected}
                        onClick={() => openReviewPreview(review)}
                      >
                        <ReviewType title={`3D · ${review.dicomIcid} · FRAME ${review.frameIndex}`}>
                          3D · {review.dicomIcid} · FRAME {review.frameIndex}
                        </ReviewType>
                        <ReviewCardMessage
                          $empty={!reviewCardMessage}
                          title={reviewCardMessage || undefined}
                        >
                          {reviewCardMessage || 'No message'}
                        </ReviewCardMessage>
                      </ReviewButton>
                    );
                  })}
                  {section.isLoading && <ReviewListMessage>Loading reviews…</ReviewListMessage>}
                  {section.error && <ReviewListMessage>Could not load reviews.</ReviewListMessage>}
                  {!section.isLoading && !section.error && section.reviews.length === 0 && (
                    <ReviewListMessage>{section.emptyMessage}</ReviewListMessage>
                  )}
                </ReviewListGroup>
              ))}
            </ReviewList>
          </ReviewListSection>
        </StudyPanel>
        <ViewerShell>
          <TopBar>
            <ButtonGroup aria-label="3D display visibility controls">
              {sources.map(({ id, label, color }) => {
                const total = sourceTotals[id];
                const hasMoreAvailable =
                  total !== undefined && total > segmentationsBySource[id].length;
                return (
                  <ToggleButton
                    key={id}
                    type="button"
                    $active={sourceVisibility[id]}
                    $color={color}
                    aria-pressed={sourceVisibility[id]}
                    onClick={() =>
                      setSourceVisibility(current => ({ ...current, [id]: !current[id] }))
                    }
                  >
                    {label} · {segmentationsBySource[id].length}
                    {total !== undefined ? `/${total}` : ''}
                    {hasMoreAvailable ? ' · more available' : ''}
                  </ToggleButton>
                );
              })}
              <ToggleButton
                type="button"
                $active={surfacesVisible}
                $color="#9ee6b8"
                aria-pressed={surfacesVisible}
                onClick={() => setSurfacesVisible(current => !current)}
              >
                SURFACE {surfacesVisible ? 'ON' : 'OFF'}
              </ToggleButton>
              <ToggleButton
                type="button"
                $active={inReviewFramesVisible}
                $color="#ffb3c1"
                aria-pressed={inReviewFramesVisible}
                title={`${inReviewFramesVisible ? 'Hide' : 'Show'} segmentations from frames currently in review`}
                onClick={() => setInReviewFramesVisible(current => !current)}
              >
                IN-REVIEW FRAMES {inReviewFramesVisible ? 'ON' : 'OFF'} · {inReviewFrameKeys.size}
              </ToggleButton>
            </ButtonGroup>
            <SegmentationLoadGroup>
              <SegmentationPageCount>
                {displayedSegmentationCount.toLocaleString()} segmentations on page
              </SegmentationPageCount>
              <StudyLabel
                type="button"
                disabled={!hasMoreSegmentations || isLoading}
                title={
                  hasMoreSegmentations
                    ? `Add ${SEGMENTATION_BATCH_SIZE} more segmentations`
                    : 'All available segmentations are loaded'
                }
                onClick={() => setSegmentationLimit(current => current + SEGMENTATION_BATCH_SIZE)}
              >
                +{SEGMENTATION_BATCH_SIZE} segmentations
              </StudyLabel>
            </SegmentationLoadGroup>
          </TopBar>
          <div ref={canvasHostRef} aria-label="Interactive 3D segmentation model" />
          {framePreview && (
            <FramePreviewPanel aria-label="Selected contour frame preview">
              <FramePreviewHeader>
                <FramePreviewMetadata>
                  <strong title={`${framePreview.dicomIcid} · Frame ${framePreview.frameIndex}`}>
                    {framePreview.dicomIcid} · Frame {framePreview.frameIndex}
                  </strong>
                  <span
                    className="segmentation-class"
                    title={`${framePreview.segmentationClass}${framePreview.userEmail ? ` · ${framePreview.userEmail}` : ''}`}
                  >
                    {framePreview.segmentationClass}
                    {framePreview.userEmail && ` · ${framePreview.userEmail}`}
                  </span>
                  <FramePreviewLink
                    href={`${LABELER_URL}/${encodeURIComponent(
                      framePreview.studyIcid
                    )}/${encodeURIComponent(framePreview.dicomIcid)}/frame/${framePreview.frameIndex}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Labeler ↗
                  </FramePreviewLink>
                  <FrameReviewButton
                    type="button"
                    onClick={() => {
                      setReviewMessage(
                        selectedFrameReview?.comment || selectedFrameReview?.reviewerComment || ''
                      );
                      const assignedOwner = annotationSetOwners.find(
                        owner => owner.userEmail === selectedFrameReview?.userEmail
                      );
                      setSelectedAnnotationSetUuid(
                        assignedOwner?.uuid ||
                          selectedAnnotationSetUuid ||
                          annotationSetOwners[0]?.uuid ||
                          ''
                      );
                      setReviewError(null);
                      setIsReviewFormOpen(true);
                    }}
                  >
                    Review Frame
                  </FrameReviewButton>
                </FramePreviewMetadata>
                <FramePreviewCloseButton
                  type="button"
                  aria-label="Close frame preview"
                  onClick={() => setFramePreview(null)}
                >
                  ×
                </FramePreviewCloseButton>
              </FramePreviewHeader>
              <FramePreviewMedia>
                {framePreview.file && (
                  <FramePreviewImage
                    src={framePreview.file}
                    alt={`DICOM ${framePreview.dicomIcid}, frame ${framePreview.frameIndex}`}
                    onLoad={event => {
                      const image = event.currentTarget;
                      setPreviewImageDimensions({
                        width: image.naturalWidth || IMAGE_WIDTH,
                        height: image.naturalHeight || IMAGE_HEIGHT,
                      });
                    }}
                  />
                )}
                {framePreview.status === 'complete' && (
                  <FramePreviewOverlay
                    viewBox={`0 0 ${previewImageDimensions.width} ${previewImageDimensions.height}`}
                    preserveAspectRatio="xMidYMid meet"
                    aria-label="Segmentation overlay"
                  >
                    {framePreview.paths.map((path, index) => (
                      <path
                        key={`${framePreview.dicomIcid}-${framePreview.frameIndex}-${index}`}
                        d={path.replace(/,/g, ' ')}
                        fill={framePreview.color}
                        fillOpacity="0.45"
                        stroke={framePreview.color}
                        strokeWidth="2"
                      />
                    ))}
                  </FramePreviewOverlay>
                )}
                {framePreview.status === 'loading' && (
                  <FramePreviewMessage>Loading frame…</FramePreviewMessage>
                )}
                {framePreview.status === 'error' && (
                  <FramePreviewMessage>Could not load this frame.</FramePreviewMessage>
                )}
              </FramePreviewMedia>
              {isReviewFormOpen && (
                <FrameReviewForm onSubmit={handleReviewFrame}>
                  <label>
                    Message · review type 3D
                    <textarea
                      value={reviewMessage}
                      onChange={event => setReviewMessage(event.target.value)}
                      placeholder="Add a message about this frame"
                      autoFocus
                    />
                  </label>
                  <AnnotationOwnerChoices>
                    <legend>Annotation set owner</legend>
                    {annotationSetOwners.map(owner => (
                      <AnnotationOwnerChoice key={owner.uuid}>
                        <input
                          type="radio"
                          name="annotation-set-owner"
                          value={owner.uuid}
                          checked={selectedAnnotationSetUuid === owner.uuid}
                          disabled={Boolean(
                            selectedFrameReview?.userEmail &&
                              owner.userEmail !== selectedFrameReview.userEmail
                          )}
                          onChange={() => setSelectedAnnotationSetUuid(owner.uuid)}
                        />
                        <span>
                          {owner.userName || owner.userEmail}
                          {owner.name ? ` · ${owner.name}` : ''}
                        </span>
                      </AnnotationOwnerChoice>
                    ))}
                    {!areAnnotationSetOwnersLoading && annotationSetOwners.length === 0 && (
                      <span>No annotation set owner is available for this study.</span>
                    )}
                    {areAnnotationSetOwnersLoading && <span>Loading owners…</span>}
                    {selectedFrameReview?.userEmail && (
                      <span>Existing reviews keep their assigned owner.</span>
                    )}
                  </AnnotationOwnerChoices>
                  {reviewError && <p role="alert">{reviewError}</p>}
                  <FrameReviewActions>
                    <button type="submit" disabled={isSavingReview || !selectedAnnotationSetUuid}>
                      {isSavingReview ? 'Saving…' : 'Save Review'}
                    </button>
                    <button
                      type="button"
                      disabled={isSavingReview}
                      onClick={() => setIsReviewFormOpen(false)}
                    >
                      Cancel
                    </button>
                  </FrameReviewActions>
                </FrameReviewForm>
              )}
            </FramePreviewPanel>
          )}
          {!isWebGlAvailable && (
            <Fallback>WebGL is required to display the interactive 3D study.</Fallback>
          )}
          {isWebGlAvailable && isLoading && displayedSegmentationCount === 0 && (
            <Fallback>Loading segmentations…</Fallback>
          )}
          {isWebGlAvailable && hasLoadError && displayedSegmentationCount === 0 && (
            <Fallback>
              Could not load the study segmentations. Check the Labeler API URL and access.
            </Fallback>
          )}
          <AnatomyBar aria-label="Anatomy visibility">
            <ToggleButton
              type="button"
              $active={false}
              $color="#ffffff"
              aria-label="Hide all anatomy"
              onClick={() => {
                setAllAnatomyHidden(true);
                setAnatomyVisibility(
                  Object.fromEntries(anatomyNames.map(className => [className, false]))
                );
              }}
            >
              HIDE ALL
            </ToggleButton>
            <ToggleButton
              type="button"
              $active={true}
              $color="#ffffff"
              aria-label="Show all anatomy"
              onClick={() => {
                setAllAnatomyHidden(false);
                setAnatomyVisibility(
                  Object.fromEntries(anatomyNames.map(className => [className, true]))
                );
              }}
            >
              SHOW ALL
            </ToggleButton>
            {anatomyNames.map(className => (
              <ToggleButton
                key={className}
                type="button"
                $active={anatomyVisibility[className] ?? true}
                $color={classColors[className]}
                aria-pressed={anatomyVisibility[className] ?? true}
                onClick={() => {
                  setAllAnatomyHidden(false);
                  setAnatomyVisibility(current => ({
                    ...current,
                    [className]: !(current[className] ?? true),
                  }));
                }}
              >
                {className}
              </ToggleButton>
            ))}
          </AnatomyBar>
          {isWebGlAvailable && (
            <Hint>
              Hover a contour to isolate · Click to preview frame · Drag to orbit · Scroll to zoom
            </Hint>
          )}
        </ViewerShell>
      </ViewerWorkspace>
    </ViewerPage>
  );
};

export default ThreeDStudyViewer;
